import React, { useState, useEffect, useCallback } from "react"
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Paper,
  Avatar,
  Chip,
  Skeleton,
  Container,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material"
import { alpha, useTheme } from "@mui/material/styles"
import {
  VideoCall as VideoCallIcon,
  CalendarTodayOutlined as CalendarIcon,
  AccessTimeOutlined as TimeIcon,
  ReportProblem as ReportProblemIcon,
  EventBusy as EventBusyIcon,
  Language as LanguageIcon,
  Email as EmailIcon,
  LocationOn as LocationOnIcon,
  Business as BusinessIcon,
  Category as CategoryIcon,
  Code as CodeIcon,
  LaptopChromebookOutlined as OnlineMeetingIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  WorkspacePremiumOutlined as WorkspacePremiumIcon,
} from "@mui/icons-material"
import Layout from "../common/Layout"
import { useNotification } from "../../context/NotificationContext"
import apiClient from "../../context/apiClient"

const formatInterviewData = (apiInterview) => {
  const companyProfile = apiInterview.company_profile || {}
  const companyName = apiInterview.company_name || companyProfile.company_name || "The Hiring Company"

  const scheduledDate = new Date(`${apiInterview.date}T${apiInterview.time}`)
  let status = apiInterview.status?.toLowerCase() || "unknown"
  let isExpired = false

  if (status === "scheduled" && !isNaN(scheduledDate) && scheduledDate < new Date()) {
    status = "cancelled"
    isExpired = true
  }

  const formattedDate = isNaN(scheduledDate)
    ? "Invalid Date"
    : scheduledDate.toLocaleDateString("en-US", {
        weekday: "short",
        month: "long",
        day: "numeric",
        year: "numeric",
      })

  const formattedTime = isNaN(scheduledDate)
    ? "Invalid Time"
    : scheduledDate.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }).toLowerCase()

  const locationValue = apiInterview.location || ""
  const urlRegex = /(https?:\/\/[^\s]+)/
  const urlMatch = locationValue.match(urlRegex)

  let interviewLink = null
  let isRemote = false

  if (urlMatch) {
    isRemote = true
    interviewLink = urlMatch[0]
  }

  return {
    id: apiInterview.id,
    companyName: companyName,
    companyAvatar: companyProfile.logo,
    companyInitial: companyName.charAt(0),
    position: apiInterview.job_title || "Job Interview",
    status: status,
    isExpired: isExpired,
    date: formattedDate,
    time: formattedTime,
    isRemote: isRemote,
    interviewLink: interviewLink,
    displayLocation: isRemote ? "Online Meeting" : locationValue || "Location TBD",
    technicalType: apiInterview.type || "General",
    skills: apiInterview.skills || [],
  }
}

const formatCompanyData = (companyProfile) => {
  if (!companyProfile) return null

  return {
    id: companyProfile.id,
    name: companyProfile.company_name || "Unknown Company",
    logoUrl: companyProfile.logo,
    location: companyProfile.location || "N/A",
    industry: companyProfile.industry?.replace(/_/g, " ") || "N/A",
    websiteUrl: companyProfile.website,
    email: null,
  }
}

const formatOfferData = (application) => {
  const jobDetails = application.job_posting_details || {}
  const companyDetails = jobDetails.company_details || {}

  const salaryMin = jobDetails.salary_min ? parseFloat(jobDetails.salary_min).toLocaleString() : "N/A"
  const salaryMax = jobDetails.salary_max ? parseFloat(jobDetails.salary_max).toLocaleString() : "N/A"
  const currency = jobDetails.salary_currency || ""

  let salaryRange = "Not Disclosed"
  if (jobDetails.salary_min && jobDetails.salary_max) {
    salaryRange = `${currency} ${salaryMin} - ${salaryMax}`
  } else if (jobDetails.salary_min) {
    salaryRange = `From ${currency} ${salaryMin}`
  }

  return {
    id: application.id,
    companyName: companyDetails.company_name || "A Company",
    companyLogo: companyDetails.logo,
    jobTitle: jobDetails.title || "Position",
    location: jobDetails.location || "N/A",
    salary: salaryRange,
    applicationDate: new Date(application.application_date).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    }),
    status: application.status,
  }
}

const InterviewCardSkeleton = () => (
  <Grid item xs={12} sm={6}>
    <Card sx={{ borderRadius: 4, p: 2 }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <Skeleton variant="circular" width={48} height={48} sx={{ mr: 2 }} />
          <Box sx={{ flexGrow: 1 }}>
            <Skeleton variant="text" sx={{ fontSize: "1.25rem" }} width="70%" />
            <Skeleton variant="text" sx={{ fontSize: "1rem" }} width="50%" />
          </Box>
        </Box>
        <Stack spacing={2} sx={{ mb: 3 }}>
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="50%" />
          <Skeleton variant="text" width="70%" />
          <Skeleton variant="text" width="80%" />
        </Stack>
      </CardContent>
      <CardActions sx={{ px: 2, pb: 2 }}>
        <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 2 }} />
      </CardActions>
    </Card>
  </Grid>
)

const CompanyCardSkeleton = () => (
  <Grid item xs={12} sm={6} md={4}>
    <Card sx={{ height: 280, width: "100%", display: "flex", flexDirection: "column" }}>
      <CardContent sx={{ flexGrow: 1, p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <Skeleton variant="circular" width={56} height={56} sx={{ mr: 2 }} />
          <Box sx={{ flexGrow: 1 }}>
            <Skeleton variant="text" sx={{ fontSize: "1.5rem" }} width="80%" />
            <Skeleton variant="text" sx={{ fontSize: "1rem" }} width="60%" />
          </Box>
        </Box>
        <Skeleton variant="text" width="40%" sx={{ mb: 1 }} />
        <Skeleton variant="text" width="70%" />
      </CardContent>
      <CardActions sx={{ p: 3, pt: 0 }}>
        <Skeleton variant="rectangular" width={100} height={36} sx={{ mr: 1 }} />
        <Skeleton variant="rectangular" width={80} height={36} />
      </CardActions>
    </Card>
  </Grid>
)

const OfferTableRowSkeleton = () => (
  <TableRow>
    <TableCell>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
        <Skeleton variant="text" width={120} />
      </Box>
    </TableCell>
    <TableCell>
      <Skeleton variant="text" />
    </TableCell>
    <TableCell>
      <Skeleton variant="text" />
    </TableCell>
    <TableCell>
      <Skeleton variant="text" />
    </TableCell>
    <TableCell>
      <Skeleton variant="text" />
    </TableCell>
    <TableCell>
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Skeleton variant="rectangular" width={80} height={36} />
        <Skeleton variant="rectangular" width={80} height={36} />
      </Stack>
    </TableCell>
  </TableRow>
)

const TalentInterviewDashboard = () => {
  const theme = useTheme()
  const { showNotification } = useNotification()
  const [mainTabValue, setMainTabValue] = useState(0)
  const [interviewSubTabValue, setInterviewSubTabValue] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [interviews, setInterviews] = useState([])
  const [companies, setCompanies] = useState([])
  const [offers, setOffers] = useState([])

  const fetchData = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const [interviewsResponse, applicationsResponse] = await Promise.all([
        apiClient.get("/talent/interviews/"),
        apiClient.get("/applications/"),
      ])

      const interviewData = interviewsResponse.data || []
      setInterviews(interviewData.map(formatInterviewData))

      const applicationsData = applicationsResponse.data || []
      const offerData = applicationsData.filter((app) => app.status === "OFFER_EXTENDED").map(formatOfferData)
      setOffers(offerData)

      const companyMap = new Map()
      interviewData.forEach((interview) => {
        if (interview.company_profile && !companyMap.has(interview.company_profile.id)) {
          companyMap.set(interview.company_profile.id, interview.company_profile)
        }
      })

      applicationsData.forEach((app) => {
        if (app.job_posting_details?.company_details && !companyMap.has(app.job_posting_details.company_details.id)) {
          companyMap.set(app.job_posting_details.company_details.id, app.job_posting_details.company_details)
        }
      })

      const uniqueCompanies = Array.from(companyMap.values())
      setCompanies(uniqueCompanies.map(formatCompanyData))
    } catch (err) {
      // More specific error message for network issues
      const errorMessage = "A network error occurred. Please check your connection and try again."
      setError(errorMessage)
      showNotification(errorMessage, "error")
      console.error("Error fetching dashboard data:", err)
    } finally {
      setIsLoading(false)
    }
  }, [showNotification])

  // Changed the dependency array from [fetchData] to []
  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleMainTabChange = (event, newValue) => {
    setMainTabValue(newValue)
  }

  const handleOfferResponse = async (applicationId, newStatus) => {
    const action = newStatus === "HIRED" ? "accepting" : "rejecting"
    try {
      await apiClient.post(`/applications/${applicationId}/update-status/`, { status: newStatus })
      showNotification(`Offer successfully ${action === "accepting" ? "accepted" : "rejected"}.`, "success")
      fetchData()
    } catch (err) {
      const errorMessage = `Failed in ${action} the offer. Please try again.`
      showNotification(errorMessage, "error")
      console.error(`Error ${action} offer:`, err)
    }
  }

  // ... the rest of your component code remains the same
  
  const renderInterviewView = () => {
    const filteredInterviews = interviews.filter((interview) => {
      if (interviewSubTabValue === 0) return interview.status === "scheduled"
      if (interviewSubTabValue === 1) return interview.status === "completed"
      if (interviewSubTabValue === 2) return interview.status === "cancelled"
      return true
    })

    if (isLoading) {
      return (
        <Grid container spacing={3}>
          <InterviewCardSkeleton />
          <InterviewCardSkeleton />
          <InterviewCardSkeleton />
          <InterviewCardSkeleton />
        </Grid>
      )
    }

    if (error) {
      return (
        <Paper variant="outlined" sx={{ p: 6, textAlign: "center", borderColor: "error.light" }}>
          <ReportProblemIcon sx={{ fontSize: 64, color: "error.main", mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Error Loading Data
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {error}
          </Typography>
        </Paper>
      )
    }

    const subTabs = [
      { label: "Scheduled", color: theme.palette.primary.main },
      { label: "Completed", color: theme.palette.success.main },
      { label: "Cancelled", color: theme.palette.error.main },
    ];

    return (
      <Box>
        <Stack direction="row" spacing={2} sx={{ display: "flex", justifyContent: "center", mb: 6 }}>
          {subTabs.map((tab, index) => (
            <Button
              key={tab.label}
              onClick={() => setInterviewSubTabValue(index)}
              sx={{
                bgcolor: interviewSubTabValue === index ? alpha(tab.color, 0.1) : "transparent",
                color: interviewSubTabValue === index ? tab.color : "text.secondary",
                border: theme => `1px solid ${interviewSubTabValue === index ? tab.color : alpha(theme.palette.divider, 0.1)}`,
                borderRadius: "12px",
                px: 4,
                py: 1,
                textTransform: "none",
                fontWeight: 700,
                transition: "all 0.2s",
                "&:hover": {
                  bgcolor: alpha(tab.color, 0.05),
                  borderColor: tab.color,
                },
              }}
            >
              {tab.label}
            </Button>
          ))}
        </Stack>


        <Grid container spacing={3}>
          {filteredInterviews.length > 0 ? (
            filteredInterviews.map((interview) => {
              const statusStyles = {
                scheduled: {
                  borderColor: "#4F46BA",
                  buttonBg: "#4F46BA",
                  chipBg: "#E8E7FF",
                  chipColor: "#4F46BA",
                },
                completed: {
                  borderColor: "transparent",
                  buttonBg: "#1E8E3E",
                  chipBg: "#E6F4EA",
                  chipColor: "#1E8E3E",
                },
                cancelled: {
                  borderColor: "transparent",
                  buttonBg: "#D93025",
                  chipBg: "#FCE8E6",
                  chipColor: "#D93025",
                },
              }
              const currentStyle = statusStyles[interview.status] || {}

              let buttonText = `Interview ${interview.status}`
              if (interview.status === "scheduled") buttonText = "Join Interview"
              if (interview.status === "completed") buttonText = "Interview Completed"
              if (interview.status === "cancelled") buttonText = "Interview Cancelled"
              if (interview.isExpired) buttonText = "Interview Expired"

              const tabColor = currentStyle.chipColor || theme.palette.primary.main

              return (
                <Grid item xs={12} sm={6} key={interview.id}>
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: "24px",
                      bgcolor: "background.paper",
                      border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: theme => `0 12px 30px ${alpha(theme.palette.common.black, 0.08)}`,
                        borderColor: "primary.main",
                        transform: "translateY(-4px)"
                      }
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 3 }}>
                        <Box sx={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}>
                          <Avatar 
                            src={interview.companyAvatar} 
                            sx={{ 
                              width: 56, 
                              height: 56, 
                              mr: 2, 
                              flexShrink: 0,
                              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                              color: "primary.main",
                              border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                            }}
                          >
                            {!interview.companyAvatar && interview.companyInitial}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography noWrap variant="h6" sx={{ fontWeight: 800, color: "text.primary", fontSize: "1.1rem" }}>
                              {interview.companyName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                              {interview.position}
                            </Typography>
                          </Box>
                        </Box>
                        <Chip
                          label={interview.isExpired ? "Expired" : interview.status}
                          sx={{
                            textTransform: "uppercase",
                            fontWeight: 800,
                            letterSpacing: 0.5,
                            fontSize: "0.7rem",
                            borderRadius: "8px",
                            bgcolor: (theme) => alpha(tabColor, 0.1),
                            color: tabColor,
                            border: (theme) => `1px solid ${alpha(tabColor, 0.2)}`,
                          }}
                        />
                      </Box>
                      <Stack spacing={2}>
                        {[
                          { icon: <CalendarIcon />, text: interview.date },
                          { icon: <TimeIcon />, text: interview.time },
                          { icon: <CategoryIcon />, text: `${interview.technicalType} Round` },
                          { icon: interview.isRemote ? <OnlineMeetingIcon /> : <LocationOnIcon />, text: interview.displayLocation },
                        ].map((item, idx) => (
                          <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            {React.cloneElement(item.icon, { sx: { fontSize: 20, color: (theme) => alpha(theme.palette.primary.main, 0.5) } })}
                            <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }}>
                              {item.text}
                            </Typography>
                          </Box>
                        ))}
                        {interview.skills.length > 0 && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CodeIcon sx={{ fontSize: 20, color: (theme) => alpha(theme.palette.primary.main, 0.5) }} />
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {interview.skills.map(skill => (
                                <Chip 
                                  key={skill} 
                                  label={skill} 
                                  size="small" 
                                  sx={{ 
                                    height: 20, 
                                    fontSize: '0.65rem', 
                                    fontWeight: 700,
                                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05)
                                  }} 
                                />
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Stack>
                    </CardContent>
                    <Box sx={{ p: 2, pt: 0 }}>
                      {interview.status === "scheduled" && interview.isRemote && interview.interviewLink ? (
                        <Button
                          variant="contained"
                          fullWidth
                          href={interview.interviewLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            bgcolor: "grey.900",
                            color: "primary.main",
                            textTransform: "none",
                            borderRadius: "12px",
                            fontWeight: 800,
                            py: 1.2,
                            border: theme => `1px solid ${theme.palette.primary.main}`,
                            "&:hover": { 
                              bgcolor: "primary.main",
                              color: "common.black"
                            },
                          }}
                        >
                          {buttonText}
                        </Button>
                      ) : (
                        <Button
                          variant="outlined"
                          fullWidth
                          disabled={interview.status !== "scheduled" || interview.displayLocation === "Location TBD"}
                          href={!interview.isRemote ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(interview.displayLocation)}` : undefined}
                          target={!interview.isRemote ? "_blank" : undefined}
                          rel={!interview.isRemote ? "noopener noreferrer" : undefined}
                          sx={{
                            textTransform: "none",
                            borderRadius: "12px",
                            fontWeight: 700,
                            py: 1.2,
                            borderColor: (theme) => alpha(theme.palette.divider, 0.2),
                            "&:disabled": {
                              bgcolor: (theme) => alpha(theme.palette.divider, 0.05),
                              color: "text.disabled",
                            }
                          }}
                        >
                          {buttonText}
                        </Button>
                      )}
                    </Box>
                  </Card>
                </Grid>

              )
            })
          ) : (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 8, textAlign: "center", bgcolor: "transparent", borderRadius: 3 }}>
                <EventBusyIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  No Interviews Here
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  You don't have any interviews in this category.
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Box>
    )
  }

  const renderOfferView = () => {
    if (isLoading) {
      return (
        <Paper
          elevation={0}
          sx={{
            borderRadius: "20px",
            overflow: "hidden",
            border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            bgcolor: theme => alpha(theme.palette.background.paper, 0.6),
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                {["Company", "Position", "Location", "Salary", "Date Received", "Actions"].map((h) => (
                  <TableCell key={h} align={h === "Actions" ? "right" : "left"}
                    sx={{ fontWeight: 800, fontSize: "0.7rem", letterSpacing: "0.08em", textTransform: "uppercase", color: "text.secondary", py: 2, borderBottom: theme => `1px solid ${alpha(theme.palette.primary.main, 0.1)}`, bgcolor: theme => alpha(theme.palette.primary.main, 0.04) }}
                  >{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {[...Array(4)].map((_, index) => (
                <OfferTableRowSkeleton key={index} />
              ))}
            </TableBody>
          </Table>
        </Paper>
      )
    }

    if (error) {
      return (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: "center",
            borderRadius: "20px",
            border: theme => `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
            bgcolor: theme => alpha(theme.palette.error.main, 0.04),
          }}
        >
          <ReportProblemIcon sx={{ fontSize: 64, color: "error.main", mb: 2, opacity: 0.7 }} />
          <Typography variant="h6" fontWeight={700} gutterBottom>Error Loading Offers</Typography>
          <Typography variant="body2" color="text.secondary">{error}</Typography>
        </Paper>
      )
    }

    if (offers.length === 0) {
      return (
        <Paper
          elevation={0}
          sx={{
            p: 8,
            textAlign: "center",
            borderRadius: "20px",
            border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            bgcolor: theme => alpha(theme.palette.background.paper, 0.5),
          }}
        >
          <WorkspacePremiumIcon sx={{ fontSize: 72, color: "primary.main", mb: 2, opacity: 0.4 }} />
          <Typography variant="h6" fontWeight={800} gutterBottom>No Offers Yet</Typography>
          <Typography variant="body2" color="text.secondary">
            When you receive an offer from a company, it will appear here.
          </Typography>
        </Paper>
      )
    }

    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: "20px",
          overflow: "hidden",
          border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
          bgcolor: theme => alpha(theme.palette.background.paper, 0.6),
          backdropFilter: "blur(12px)",
        }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="offers table">
          <TableHead>
            <TableRow sx={{ bgcolor: theme => alpha(theme.palette.primary.main, 0.06) }}>
              {["Company", "Position", "Location", "Salary", "Date Received"].map((h) => (
                <TableCell key={h}
                  sx={{ fontWeight: 800, fontSize: "0.7rem", letterSpacing: "0.08em", textTransform: "uppercase", color: "text.secondary",
                    borderBottom: theme => `1px solid ${alpha(theme.palette.primary.main, 0.12)}`, py: 2 }}
                >{h}</TableCell>
              ))}
              <TableCell align="right"
                sx={{ fontWeight: 800, fontSize: "0.7rem", letterSpacing: "0.08em", textTransform: "uppercase", color: "text.secondary",
                  borderBottom: theme => `1px solid ${alpha(theme.palette.primary.main, 0.12)}`, py: 2 }}
              >Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {offers.map((offer) => (
              <TableRow
                key={offer.id}
                sx={{
                  transition: "background 0.2s ease",
                  "&:hover": { bgcolor: theme => alpha(theme.palette.primary.main, 0.04) },
                  "&:last-child td, &:last-child th": { border: 0 },
                  "& td": { borderBottom: theme => `1px solid ${alpha(theme.palette.divider, 0.06)}` },
                }}
              >
                <TableCell component="th" scope="row" sx={{ py: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Avatar
                      src={offer.companyLogo}
                      sx={{
                        width: 42,
                        height: 42,
                        border: theme => `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                        bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                        color: "primary.main",
                        fontWeight: 800,
                      }}
                    >
                      {!offer.companyLogo && offer.companyName?.charAt(0)}
                    </Avatar>
                    <Typography variant="body2" fontWeight={700}>{offer.companyName}</Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ py: 2 }}>
                  <Typography variant="body2" fontWeight={600}>{offer.jobTitle}</Typography>
                </TableCell>
                <TableCell sx={{ py: 2 }}>
                  <Typography variant="body2" color="text.secondary">{offer.location}</Typography>
                </TableCell>
                <TableCell sx={{ py: 2 }}>
                  <Chip
                    label={offer.salary || "Negotiable"}
                    size="small"
                    sx={{
                      bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                      color: "primary.main",
                      fontWeight: 800,
                      fontSize: "0.75rem",
                      borderRadius: "8px",
                    }}
                  />
                </TableCell>
                <TableCell sx={{ py: 2 }}>
                  <Typography variant="body2" color="text.secondary">{offer.applicationDate}</Typography>
                </TableCell>
                <TableCell align="right" sx={{ py: 2 }}>
                  <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<CheckCircleIcon />}
                      onClick={() => handleOfferResponse(offer.id, "HIRED")}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        borderRadius: "10px",
                        px: 2,
                        bgcolor: theme => alpha(theme.palette.success.main, 0.15),
                        color: "success.light",
                        boxShadow: "none",
                        border: theme => `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                        "&:hover": { bgcolor: "success.main", color: "common.white", boxShadow: "none" },
                      }}
                    >
                      Accept
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<CancelIcon />}
                      onClick={() => handleOfferResponse(offer.id, "OFFER_REJECTED")}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        borderRadius: "10px",
                        px: 2,
                        color: "error.light",
                        borderColor: theme => alpha(theme.palette.error.main, 0.3),
                        "&:hover": { bgcolor: theme => alpha(theme.palette.error.main, 0.1), borderColor: "error.main" },
                      }}
                    >
                      Decline
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    )
  }

  const renderCompanyView = () => {
    if (isLoading) {
      return (
        <Grid container spacing={3}>
          {[...Array(6)].map((_, index) => (
            <CompanyCardSkeleton key={index} />
          ))}
        </Grid>
      )
    }

    if (error) {
      return (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: "center",
            borderRadius: "20px",
            border: theme => `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
            bgcolor: theme => alpha(theme.palette.error.main, 0.04),
          }}
        >
          <ReportProblemIcon sx={{ fontSize: 64, color: "error.main", mb: 2, opacity: 0.7 }} />
          <Typography variant="h6" fontWeight={700} gutterBottom>Error Loading Data</Typography>
          <Typography variant="body2" color="text.secondary">{error}</Typography>
        </Paper>
      )
    }

    return (
      <Grid container spacing={3}>
        {companies.length > 0 ? (
          companies.map((company) => (
            <Grid item xs={12} sm={6} md={4} key={company.id}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  width: "100%",
                  borderRadius: "20px",
                  border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                  bgcolor: theme => alpha(theme.palette.background.paper, 0.6),
                  backdropFilter: "blur(12px)",
                  transition: "all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: theme => `0 16px 40px ${alpha(theme.palette.common.black, 0.2)}`,
                    border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
                  },
                }}
              >
                <CardContent sx={{ p: 3.5, flexGrow: 1 }}>
                  {/* Header Row */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                    <Avatar
                      src={company.logoUrl}
                      sx={{
                        width: 56,
                        height: 56,
                        border: theme => `2px solid ${alpha(theme.palette.primary.main, 0.35)}`,
                        bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                        color: "primary.main",
                        fontWeight: 900,
                        fontSize: "1.4rem",
                        flexShrink: 0,
                      }}
                    >
                      {!company.logoUrl && <BusinessIcon sx={{ fontSize: 28 }} />}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight={800}
                        sx={{ lineHeight: 1.2, mb: 0.4 }}
                        noWrap
                      >
                        {company.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {company.location}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Industry Chip */}
                  {company.industry && (
                    <Chip
                      label={company.industry}
                      size="small"
                      sx={{
                        mb: 2,
                        bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                        color: "primary.main",
                        fontWeight: 700,
                        fontSize: "0.72rem",
                        letterSpacing: "0.04em",
                        borderRadius: "8px",
                        border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      }}
                    />
                  )}

                  {/* Additional Info */}
                  {company.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        lineHeight: 1.6,
                        fontSize: "0.82rem",
                      }}
                    >
                      {company.description}
                    </Typography>
                  )}
                </CardContent>

                <CardActions
                  sx={{
                    px: 3.5,
                    pb: 3.5,
                    pt: 0,
                    borderTop: theme => `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                    mt: "auto",
                  }}
                >
                  <Box sx={{ display: "flex", gap: 1.5, width: "100%", pt: 1.5 }}>
                    {company.websiteUrl && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<LanguageIcon />}
                        href={company.websiteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          flex: 1,
                          textTransform: "none",
                          fontWeight: 700,
                          borderRadius: "10px",
                          borderColor: theme => alpha(theme.palette.primary.main, 0.3),
                          color: "primary.main",
                          "&:hover": {
                            bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                            borderColor: "primary.main",
                          },
                        }}
                      >
                        Website
                      </Button>
                    )}
                    {company.email && (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<EmailIcon />}
                        href={`mailto:${company.email}`}
                        sx={{
                          flex: 1,
                          textTransform: "none",
                          fontWeight: 700,
                          borderRadius: "10px",
                          bgcolor: theme => alpha(theme.palette.primary.main, 0.12),
                          color: "primary.main",
                          boxShadow: "none",
                          border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                          "&:hover": { bgcolor: "primary.main", color: "common.black", boxShadow: "none" },
                        }}
                      >
                        Contact
                      </Button>
                    )}
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 8,
                textAlign: "center",
                borderRadius: "20px",
                border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                bgcolor: theme => alpha(theme.palette.background.paper, 0.4),
              }}
            >
              <BusinessIcon sx={{ fontSize: 72, color: "primary.main", mb: 2, opacity: 0.35 }} />
              <Typography variant="h6" fontWeight={800} gutterBottom>No Companies Found</Typography>
              <Typography variant="body2" color="text.secondary">
                Companies you interview with will appear here.
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
    )
  }

  return (
    <Layout title="Talent Dashboard">
      <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
        <Container maxWidth="xl" sx={{ py: { xs: 4, md: 6 } }}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: { xs: 4, md: 8 }, 
              borderRadius: "28px", 
              bgcolor: "grey.900",
              color: "common.white",
              position: "relative",
              overflow: "hidden",
              mb: 6,
              backgroundImage: theme => `radial-gradient(circle at 2% 2%, ${alpha(theme.palette.primary.main, 0.15)} 0%, transparent 40%)`,
              border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            }}
          >
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Typography 
                variant="h2" 
                component="h1" 
                sx={{ 
                  fontWeight: 900, 
                  letterSpacing: -2,
                  mb: 1,
                  background: theme => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent"
                }}
              >
                My Dashboard
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 400, opacity: 0.8, maxWidth: "600px" }}>
                Manage your professional journey. Track interviews, review offers, and connect with industry leaders.
              </Typography>
            </Box>
          </Paper>

          <Stack direction="row" spacing={2} sx={{ mb: 6, justifyContent: "center" }}>
            {[
              { id: 0, label: "Interviews", icon: <VideoCallIcon /> },
              { id: 1, label: "Offers", icon: <WorkspacePremiumIcon /> },
              { id: 2, label: "Companies", icon: <BusinessIcon /> },
            ].map((tab) => (
              <Button
                key={tab.id}
                variant={mainTabValue === tab.id ? "contained" : "outlined"}
                onClick={(e) => handleMainTabChange(e, tab.id)}
                startIcon={tab.icon}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: 700,
                  borderRadius: "18px",
                  textTransform: "none",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  bgcolor: mainTabValue === tab.id ? "primary.main" : "transparent",
                  borderColor: (theme) => alpha(theme.palette.primary.main, 0.3),
                  color: mainTabValue === tab.id ? "common.black" : "text.secondary",
                  "&:hover": {
                    bgcolor: mainTabValue === tab.id ? "primary.dark" : (theme) => alpha(theme.palette.primary.main, 0.05),
                    borderColor: "primary.main",
                    transform: "translateY(-2px)",
                    boxShadow: mainTabValue === tab.id ? (theme) => `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}` : "none"
                  }
                }}
              >
                {tab.label}
              </Button>
            ))}
          </Stack>


            {mainTabValue === 0 && renderInterviewView()}
            {mainTabValue === 1 && renderOfferView()}
            {mainTabValue === 2 && renderCompanyView()}
        </Container>
      </Box>
    </Layout>
  )
}

export default TalentInterviewDashboard
